import { Component, ErrorInfo, ReactNode } from 'react';

import { ErrorFallback } from '@/components/ErrorFallback';
import { Typography } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import i18n from '@/utils/plugins/i18n.plugin';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree.
 * Logs errors and displays a fallback UI instead of crashing the entire app.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Here you could log the error to an error reporting service (e.g., Sentry)
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error fallback UI (hors I18nextProvider : traduction via l'instance i18n)
      const { error, errorInfo } = this.state;
      const details =
        process.env.NODE_ENV === 'development' && error ? (
          <div className='max-h-52 overflow-auto rounded-md bg-muted p-4 text-left'>
            <Typography
              variant='monospaced'
              as='pre'
              color='destructive'
              className='whitespace-pre-wrap break-words text-xs'
            >
              {error.toString()}
            </Typography>
            {errorInfo?.componentStack && (
              <Typography
                variant='monospaced'
                as='pre'
                className='mt-2 whitespace-pre-wrap break-words text-xs'
              >
                {errorInfo.componentStack}
              </Typography>
            )}
          </div>
        ) : undefined;

      return (
        <div className='flex min-h-svh items-center justify-center bg-page-background p-4'>
          <ErrorFallback
            className='max-w-xl'
            title={i18n.t('error.boundary.title')}
            description={i18n.t('error.boundary.description')}
            details={details}
            actions={<Button onClick={this.handleReload}>{i18n.t('error.boundary.reload')}</Button>}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
